import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  App,
  Button,
  Flex,
  Input,
  ModalProps,
  Spin,
  Modal,
  List,
  Checkbox,
  Tag,
  Form,
  Popover,
  Progress,
  notification
} from "antd";
import { FilterOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import apis, { finishQuotationRequest, itemAdds, itemsRm } from "@/apis/requirement";
import orderApis from "@/apis/order";
import { useTranslation } from "react-i18next";
import {
  QuotationRequest,
  Requirement,
  RequirementAddItem,
  RequirementConfirm,
  RequirementConfirmShowEnum,
  RequirementConfirmTypeEnum,
  RequirementConfirmValueEnum,
  RequirementItemDelEnum
} from "@/types/requirement";
import useRequirementParams from "../../hooks/useRequirementParams";
import RequirementSteps from "../../components/RequirementSteps";
import { Supplier, SupplierLadderPrice } from "@/types/purchase";
import RequirementReview from "../../components/RequirementReview";
import { PageId, PageType } from "@/types/page";
import DetailModal from "@/components/DetailModal";
import { RequirementDetailType, useGetDetail } from "./hooks/useGetDetail";
import DetailTitle from "./components/DetailTitle";
import ProdInput, { ProdInputRef } from "./components/ProdInput";
import RequirementDesc from "./components/RequirementDesc";
import RequestPriceOpt, {
  RequestPriceOptCbEnum
} from "./components/RequestPriceOpt";
import QuoteProgressDetail from "./components/QuoteProgressDetail";
import { SaleOrderAdd, SaleOrderAddItem } from "@/types/sales";
import { TableWrapNew } from "./style";
import { PurchaseModal } from "./components/SelectedQuotation";
import DetailCellText from "./components/DetailCellText";
import SortHeader from "./components/SortHeader";
import { buildPreOutQtyMap, sumPendingOrderQuantities, updateCheckedItems } from "./tableData";
import CommonTable from "@/components/CommonTable";
import type { ColumnType } from "antd/es/table";
import useColumnDrag from "./hooks/useColumnDrag";
import AddMaterialOverIssue from "@/pages/Reimburse/components/AddMaterialOverIssue";
import { Org } from "@/types/org";
import { RootState } from "@/store";
import { orderAdd } from "@/apis/inventory";
import { orderDel, orderItemRms } from "@/apis/inventoryv2";
import { purOrderInventories, itemDel } from "@/apis/purchase";
import {
  ChangeEnum,
  OperateEnum,
  OrderAdd,
  OrderAddItem
} from "@/types/inventory";
import { ItemLabelEnum, SourceTypeEnum, TagLabel } from "@/types/common";
import { StoreTypeEnum } from "@/types/product";
import { ConfirmedEnum } from "@/types/inventory";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { DeleteFilled } from "@ant-design/icons";
import { getRequirementConfirmItem, numFormmater, requirementIsEnd } from "../../util";
import DeleteModal from "./components/DeleteModal";
import ItemInfo from "./components/ItemInfo";
import AddItem from "./components/AddItem";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import SpecialOut from "./components/SpecialOut";
import PreOutOrderPrint from "./components/PreOutOrderPrint";
import { OrderItemV2, OrderV2 } from "@/types/inventoryV2";
import MultiPurchaseSteps from "./components/MultiPurchaseSteps";
import SwapSaleOrder from "./components/MultiPurchaseSteps/SwapSaleOrder";
import MultiQuotationOpt from "./components/MultiQuotationOpt";
import RequirementPickOut from "./components/RequirementPickOut";
import InventoryList from "./components/InventoryList";
import RequirementForm, { RequirementFieldType } from "../../components/RequirementForm";
import StepsFormItem, { RequirementConfirmLabel } from "../../components/StepsFormItem";
import useReqConfirmType from "../../components/StepsFormItem/useReqConfirmType";
import WmsTags from "@/components/WmsTags";
import ImportQuotationExcel from "./components/ImportQuotationExcel";
import ListFilterBar from "@/components/ListFilterBar";
import { filterProductItemsByKeyword } from "@/utils/product";
import { MaterialSearchWrap } from "../../style";
import { minus } from "@/utils/calc";

const DELETED_CELL_STYLE: CSSProperties = { color: "red" };
const REPLACEMENT_CELL_STYLE: CSSProperties = { color: "orange" };
const PENDING_CELL_STYLE: CSSProperties = { color: "#FF4d4F" };

interface RequirementDetailProps extends ModalProps {
  id: string;
  handleOk: (type?: "close" | "del") => void;
  number: string;
  handleCancel: () => void;
  handleProgress?: (data: Requirement) => void;
  onUpdateRequirement?: (data: Requirement) => void;
  reOrder?: boolean | undefined;
  isAdmin?: boolean;
}

export interface RequirementColumnSupplier {
  prices: Array<SupplierLadderPrice>;
  supplier: Supplier;
  id: string;
}

interface OutInventory {
  productId: string;
  quantity: number;
  name: string;
}

const RequirementDetail: FC<RequirementDetailProps> = ({
  id,
  number,
  isAdmin = false,
  handleOk,
  handleCancel,
  handleProgress,
  onUpdateRequirement,
  reOrder,
  ...props
}) => {
  const { params, pageId, pageType } = useRequirementParams(true);

  const curOrg: Org = useSelector(
    (state: RootState) => state.orgReducer.curOrg
  );
  const { t } = useTranslation();
  const { modal } = App.useApp();

  const [detail, items, loading, { getSubMaterial, setDetail, setItems, setOrders }, orders] = useGetDetail({
    id,
    number,
    showDel: pageId === PageId.MC_REQ_MANAGE
  });

  const { getTypeName } = useReqConfirmType();

  const prodInputRef = useRef<ProdInputRef>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);

  // null 表示尚未打开；首次操作后保留实例，避免关闭时丢失请求中的 loading。
  const [open, setOpen] = useState<boolean | null>(null);
  const [passLoading, setPassLoading] = useState<boolean>(false);

  const [progressInfo, setProgressInfo] = useState<
    RequirementDetailType | undefined
  >(undefined);

  const [noQuotaionPros, setNoQuotaionPros] = useState<string[]>([]);
  const [quotationPreviewId, setQuotationPreviewId] = useState<string>("");
  const [scrollHeight, setScrollHeight] = useState<number>(600);
  // const [selectedRows, setSelectedRows] = useState<RequirementDetailType[]>([])
  const [openOutInventory, setOpenOutInventory] = useState<boolean>(false);
  const [inventory, setInventoryList] = useState<OutInventory[]>([]);
  const [outInventoryLoading, setOutInventoryLoading] =
    useState<boolean>(false);
  const [showList, setShowList] = useState<string>("");
  const [specialOut, setSpecialOut] = useState<string>("");
  const [openAddItem, setOpenAddItem] = useState<boolean | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean | null>(null);
  // 仅同步清除全部需求单项时展示进度，普通选中删除保持原二次确认体验。
  const [deleteProgressOpen, setDeleteProgressOpen] = useState<boolean>(false);
  const [deleteProgress, setDeleteProgress] = useState<number>(0);
  const [multiSteps, setMultiSteps] = useState<boolean>(false);
  const [swapSale, setSwapSale] = useState<boolean>(false);

  const [multiQuotation, setMultiQuotation] = useState<boolean>(false);

  const [itemDe, setItemDe] = useState<RequirementDetailType | null>(null);
  const [overIssueItem, setOverIssueItem] = useState<RequirementDetailType | null>(null);
  const [openPickOut, setOpenPickOut] = useState<boolean>(false);

  const [newConfirm, setNewConfirm] = useState<Array<RequirementConfirm>>([]);

  const [checkedProcess, setCheckedProcess] = useState<Array<string>>([]);

  // 排序状态
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc" | null;
  }>({
    key: "",
    direction: null
  });

  const stepValue: Array<RequirementConfirmLabel> | undefined = useMemo(() => {
    let stepValue: Array<RequirementConfirmLabel> | undefined = undefined;
    if (detail?.confirms) {
      stepValue = detail?.confirms.map(cfm => ({
        ...cfm,
        name: getTypeName(cfm.type)
      }))
    }
    return stepValue;
  }, [detail?.confirms])

  const hasConfirmChanged = useMemo(() => {
    if (!!!detail?.confirms) return false
    else if (newConfirm.length === 0) return false
    else {
      for (let i = 0; i < detail.confirms.length; i++) {
        const oldData = detail.confirms[i];
        const newData = newConfirm.find(con => con.type === oldData.type)
        if (newData?.show !== oldData.show || newData.sort !== oldData.sort) return true
      }
    }
    return false
  }, [newConfirm, detail?.confirms])


  const allOrderItems = useMemo(() => {
    let items: Array<OrderItemV2> = [];
    orders.forEach(order => {
      let orderItems: Array<OrderItemV2> = order.items.map(item => ({
        ...item,
        order: {
          ...order,
          items: []
        }
      }))
      items.push(...orderItems)
    })
    return items;
  }, [orders]);

  const processes = useMemo(() => {
    let res: Array<string> = [];
    items.forEach(i => {
      let process = (i.process || i.product?.process || '').split(',') || [];
      process.forEach(p => {
        if (!!p && !!p.trim() && !!!res.includes(p)) res.push(p)
      })
    })
    return res
  }, [items]);

  const processFilteredItems = useMemo(() => {
    if (checkedProcess.length === 0) return [...items];

    return items.filter(item => {
      if (pageId === PageId.MC_REQ_M && item.deleted === RequirementItemDelEnum.DEL) return false
      let itemProcess = item?.process?.split(',') || item?.product?.process?.split(',') || [];
      for (let index = 0; index < checkedProcess.length; index++) {
        const element = checkedProcess[index];
        if (itemProcess.includes(element))
          return true
      }
      return false
    })
  }, [items, checkedProcess, pageId])

  const [keyword, setKeyword] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const filteredItems = useMemo(() => filterProductItemsByKeyword(processFilteredItems, searchKeyword), [processFilteredItems, searchKeyword]);

  useEffect(() => {
    setKeyword("");
    setSearchKeyword("");
  }, [id]);

  const selectableFilteredItems = useMemo(
    () => filteredItems.filter(item => item.deleted !== RequirementItemDelEnum.DEL),
    [filteredItems]
  );
  // 筛选后达到 50 行时开启虚拟滚动。
  const virtualTable = filteredItems.length >= 50;

  const isRepTip = useMemo(() => {
    return !!items.find(data => !!data?.product?.replacement);
  }, [items]);

  useEffect(() => {
    pageId && getSubMaterial();
  }, [id, pageId]);

  useEffect(() => {
    const container = tableWrapRef.current;
    if (!container || !detail) return;

    const updateScrollHeight = () => {
      setScrollHeight(container.clientHeight || 600);
    };
    updateScrollHeight();
    const observer = new ResizeObserver(updateScrollHeight);
    observer.observe(container);
    return () => observer.disconnect();
  }, [detail, loading]);

  // isAll 表示“同步清除需求单项”：删除全部未删除项，并跳过关联领料/采购二次确认。
  const handleDeleteConfirm = async (isAll = false) => {
    try {
      // TODO: 在这里添加扩展功能
      // 第一步：获取selectedRows中的equotationRequest.purchaseItem数据
      const purchaseItems: string[] = [];

      const rowItems = isAll
        ? items.filter(item => item.deleted !== RequirementItemDelEnum.DEL)
        : selectedRows;
      const rowIds = rowItems.map(row => row.id);

      rowItems.forEach(row => {
        if (row.equotationRequest?.purchaseItem) {
          purchaseItems.push(row.equotationRequest.purchaseItem.id);
        }
      });

      // // 第二步：根据purchaseItem的id调用purOrderInventories接口
      // const orderPromises = purchaseItems.map(id => purOrderInventories(id));
      // const orderResults = await Promise.all(orderPromises);

      // // 第三步：提取所有的OrderV2数据
      // const allOrders: OrderV2[] = [];
      // orderResults.forEach(orders => {
      //   if (orders && orders?.length > 0) {
      //     orders.forEach(orderItem => {
      //       if (orderItem.order) {
      //         allOrders.push(orderItem.order);
      //       }
      //     });
      //   }
      // });

      // // 第四步：过滤出confirmState不是CONFIRM的数据
      // const unconfirmedOrders: OrderV2[] = allOrders.filter(
      //   order => order.confirmState !== ConfirmedEnum.CONFIRM
      // );
      let notDelCodes: string[] = [];
      for (let i = 0; i < rowItems.length; i++) {
        if (rowItems[i].outCount && Number(rowItems[i].outCount || 0) > 0) {
          notDelCodes.push(rowItems[i].product?.code ?? '')
        }
      }
      if (notDelCodes.length > 0 && pageId === PageId.MC_REQ_MANAGE) {
        notification.error({
          message: t('requirement.delErrTip'),
          description: notDelCodes.join(', ')
        })
        return;
      }
      let delOrderItems = allOrderItems.filter(item => rowIds.includes(item.sourceId) && item.order.confirmState !== ConfirmedEnum.CONFIRM)
      let delOrderItemsNot: OrderItemV2[] = [];
      if (pageId === PageId.MC_REQ_M) {
        delOrderItemsNot = allOrderItems.filter(item => rowIds.includes(item.sourceId) && item.order.confirmState === ConfirmedEnum.CONFIRM)
      }

      let shouldDeleteOrderItems = delOrderItems.length > 0;
      const orderItemIdsByOrderId = delOrderItems.reduce<Record<string, string[]>>((map, itemData) => {
        if (!map[itemData.orderId]) map[itemData.orderId] = [];
        map[itemData.orderId].push(itemData.id);
        return map;
      }, {});
      const totalDeleteSteps = Object.keys(orderItemIdsByOrderId).length + purchaseItems.length + 1;
      let finishedDeleteSteps = 0;
      // 全量删除按实际接口步骤推进：领料单分组、采购项、需求单项删除。
      const finishDeleteProgressStep = () => {
        if (!isAll) return;
        finishedDeleteSteps += 1;
        setDeleteProgress(Math.min(100, Math.round((finishedDeleteSteps / totalDeleteSteps) * 100)));
      };
      if (isAll) {
        setDeleteProgressOpen(true);
        setDeleteProgress(0);
      }
      // 二次弹窗提示是否确认删除对应领料单
      if (delOrderItems.length > 0 && !isAll) {
        const confirmed = await new Promise<boolean>((resolve) => {
          modal.confirm({
            title: t("common.tip"),
            content: t("requirement.delOrderItemTip"),
            okText: t("common.confirm"),
            cancelText: t("common.cancel"),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        shouldDeleteOrderItems = confirmed;
      }
      if (shouldDeleteOrderItems) {
        for (const [orderId, itemIds] of Object.entries(orderItemIdsByOrderId)) {
          await orderItemRms(orderId, itemIds)
          finishDeleteProgressStep();
        }
      }

      // 二次弹窗提示是否确认删除对应采购单（调用 itemDel）
      if (pageId === PageId.MC_REQ_MANAGE && purchaseItems.length > 0) {
        const confirmed = isAll ? true : await new Promise<boolean>((resolve) => {
          modal.confirm({
            title: t("common.tip"),
            content: t("requirement.delPurchaseOrderItemTip"),
            okText: t("common.confirm"),
            cancelText: t("common.cancel"),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (confirmed) {
          if (isAll) {
            for (let i = 0; i < purchaseItems.length; i++) {
              await itemDel(purchaseItems[i]);
              finishDeleteProgressStep();
            }
          } else {
            const itemDelPromises = purchaseItems.map(id => itemDel(id));
            await Promise.all(itemDelPromises);
          }
        }
      }

      // 执行删除操作
      await itemsRm(rowIds);
      finishDeleteProgressStep();

      // // 第五步：删除未确认的订单（调用 orderDel）
      // const orderDelPromises = unconfirmedOrders.map(order =>
      //   orderDel(order.id)
      // );
      // await Promise.all(orderDelPromises);

      const deletedRequirementIds = new Set(rowIds);
      if (shouldDeleteOrderItems) {
        const deletedOrderItemIds = new Set(delOrderItems.map(item => item.id));
        setOrders(last =>
          last.map(order => ({
            ...order,
            items: order.items.filter(orderItem => !deletedOrderItemIds.has(orderItem.id))
          }))
        );
      }
      if (pageId === PageId.MC_REQ_MANAGE) setItems(last =>
        last.map(item =>
          deletedRequirementIds.has(item.id)
            ? {
              ...item,
              deleted: RequirementItemDelEnum.DEL,
              checked: false
            }
            : item
        )
      )
      else setItems(last => last.filter(i => !deletedRequirementIds.has(i.id)))

      if (detail) {
        const nextItemCount = Math.max(
          0,
          Number(detail.itemCount || 0) - rowIds.length
        );
        const nextDetail = {
          ...detail,
          itemCount: `${nextItemCount}`,
        };
        setDetail(nextDetail);
        onUpdateRequirement?.(nextDetail);
      }

      notification.success({
        placement: "top",
        message: t("common.rmSuccess"),
        description: (
          !!delOrderItemsNot.length && <>
            <div>{t('requirement.needReturn')}</div>
            <span>{`${t('requirement.needReturnTip')}: ${delOrderItemsNot.map(i => i.product.code).join(', ')}`}</span>
          </>
        )
      });
      if (isAll) {
        setDeleteProgress(100);
        // 给 React 一次渲染 100% 的机会，避免进度条未到满格就被关闭。
        await new Promise(resolve => setTimeout(resolve, 300));
        setDeleteProgressOpen(false);
      }
    } catch (error) {
      if (isAll) setDeleteProgressOpen(false);
      notification.error({ placement: "top", message: t("common.rmError") });
      throw error;
    }
  };

  const clearSelection = useCallback(() => {
    setItems(last => updateCheckedItems(last, new Set(last.map(item => item.id)), false));
  }, [setItems]);

  const selectableItemIds = useMemo(
    () => new Set(selectableFilteredItems.map(item => item.id)),
    [selectableFilteredItems]
  );

  const changeCheck = useCallback((e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    setItems(last => updateCheckedItems(last, selectableItemIds, checked));
  }, [selectableItemIds, setItems]);

  const changeItemCheck = useCallback((id: string, checked: boolean) => {
    const ids = new Set([id]);
    setItems(last => updateCheckedItems(last, ids, checked));
  }, [setItems]);

  const { selectedIds, selectedRows } = useMemo(() => {
    let selectedIds: Array<string> = [];
    let selectedRows: Array<RequirementDetailType> = [];
    items.forEach(item => {
      if (item.checked && item.deleted !== RequirementItemDelEnum.DEL) {
        selectedIds.push(item.id);
        selectedRows.push(item);
      }
    });
    return { selectedIds, selectedRows };
  }, [items]);

  const pendingOrderQuantities = useMemo(
    () => sumPendingOrderQuantities(allOrderItems),
    [allOrderItems]
  );

  const preOutQtyMap = useMemo(
    () => buildPreOutQtyMap(items, pendingOrderQuantities),
    [items, pendingOrderQuantities]
  );

  const handleAdd = (addItems: Array<RequirementAddItem>) => {
    if (addItems.find(addI => Number(addI.purCount) > 0) === undefined) return;
    itemAdds(id, addItems)
      .then(res => {
        let newItems: RequirementDetailType[] = res.map(r => ({
          ...r,
          purchaseDesc: "",
          ertDate: "",
          outCount: "",
          checked: false
        }));
        setItems([...items, ...newItems]);
        notification.success({
          placement: "top",
          message: t("common.addSuccess")
        });
      })
      .catch(() => {
        notification.error({ placement: "top", message: t("common.addError") });
      });
  };

  const handleSortChange = useCallback((key: string) => {
    setSortConfig(previous => ({
      key,
      direction: previous.key === key && previous.direction !== "desc" ? "desc" : "asc",
    }));
  }, []);

  // 处理排序逻辑
  const handleSort = (
    data: RequirementDetailType[],
    key: string,
    direction: "asc" | "desc" | null
  ) => {
    if (!direction) return data;

    return [...data].sort((a, b) => {
      let valueA: any;
      let valueB: any;

      switch (key) {
        case "productCode":
          valueA = Number(a.product?.code ?? "") || 0;
          valueB = Number(b.product?.code ?? "") || 0;
          break;
        case "bomAmount":
          valueA = Number(a.bomAmount ?? 0);
          valueB = Number(b.bomAmount ?? 0);
          break;
        case "useAmount":
          valueA = Number(a.useAmount ?? 0);
          valueB = Number(b.useAmount ?? 0);
          break;
        case "purCount":
          valueA = Number(a.purCount ?? 0);
          valueB = Number(b.purCount ?? 0);
          break;
        case "puredCount":
          valueA = Number(a.puredCount ?? 0);
          valueB = Number(b.puredCount ?? 0);
          break;
        case "inCount":
          valueA = Number(a.inCount ?? 0);
          valueB = Number(b.inCount ?? 0);
          break;
        case "outCount":
          valueA = Number(a.outCount ?? 0);
          valueB = Number(b.outCount ?? 0);
          break;
        case "pendingOutCount":
          valueA = Number(minus(a.useAmount, a.outCount));
          valueB = Number(minus(b.useAmount, b.outCount));
          break;
        case "useFactoryCount":
          valueA = Number(a.useFactoryCount ?? 0);
          valueB = Number(b.useFactoryCount ?? 0);
          break;
        case "materialProcess":
          valueA = a.process ?? a.product?.process ?? "";
          valueB = b.process ?? b.product?.process ?? "";
          break;
        default:
          return 0;
      }

      if (valueA < valueB) return direction === "asc" ? -1 : 1;
      if (valueA > valueB) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  // 排序 items
  useEffect(() => {
    if (sortConfig.direction && sortConfig.key) {
      const sortedItems = handleSort(items, sortConfig.key, sortConfig.direction);
      if (JSON.stringify(sortedItems.map(i => i.id)) !== JSON.stringify(items.map(i => i.id))) {
        setItems(sortedItems);
      }
    }
  }, [sortConfig]);

  const handleDelItemTag = (id: string) => {
    setItems(last => last.map(i => ({ ...i, tags: i.tags?.filter(t => t.id !== id) ?? [] })))
  };

  const handelAddItemTag = (tag: TagLabel, id: string) => {
    setItems(last => last.map(i => ({ ...i, tags: i.id === id ? [...(i.tags ?? []), tag] : i.tags })))
  }

  const columns = useMemo<ColumnType<RequirementDetailType>[]>(
    () => [
      {
        key: "selection",
        title: (
          <Flex style={{ width: "100%" }} justify="flex-start" align="center">
            <Checkbox
              checked={
                selectableFilteredItems.length > 0 && selectableFilteredItems.every(item => item.checked)
              }
              onChange={changeCheck}
              style={{ marginRight: 10 }}
            />
            <DetailCellText text={`${t("common.select")}`} />
          </Flex>
        ),
        width: 80,
        fixed: "left",
        render: (_, record) => (
          <Checkbox
            checked={record.checked && record?.deleted === 0}
            disabled={record?.deleted === 1}
            onChange={e => changeItemCheck(record.id, e.target.checked)}
          />
        )
      },
      {
        dataIndex: "productCode",
        key: "productCode",
        title: <SortHeader
          text={t("product.code")}
          sortKey="productCode"
          direction={sortConfig.key === "productCode" ? sortConfig.direction : null}
          onSort={handleSortChange}
        />,
        width: 100,
        fixed: "left",
        render: (val, record, index) =>
          (record.product?.code &&
            noQuotaionPros.includes(record.product?.code)) ||
            record?.deleted === 1 ? (
            <DetailCellText style={DELETED_CELL_STYLE} text={record.product?.code || ""} />
          ) : (
            <DetailCellText
              style={record?.product?.replacement ? REPLACEMENT_CELL_STYLE : undefined}
              text={record.product?.code || "-"}
            />
          )
      },
      {
        key: "productName",
        title: <DetailCellText text={t("product.name")} />,
        width: 100,
        fixed: "left",
        render(value, row, rowIndex) {
          return (
            <DetailCellText
              style={row?.deleted === 1 ? DELETED_CELL_STYLE : undefined}
              text={row?.product?.name || ""}
            />
          );
        }
      },
      {
        key: "uom",
        title: <DetailCellText text={t("product.uom")} />,
        width: 60,
        fixed: "left",
        render(value, row, rowIndex) {
          return (
            <DetailCellText
              style={row?.deleted === 1 ? DELETED_CELL_STYLE : undefined}
              text={row?.product?.uom || ""}
            />
          );
        }
      },
      {
        key: "model",
        title: <DetailCellText text={t("product.brand1")} />,
        width: 220,
        fixed: "left",
        render(value, row, rowIndex) {
          return (
            <DetailCellText
              style={row?.deleted === 1 ? DELETED_CELL_STYLE : undefined}
              text={row?.product?.model || ""}
            />
          );
        }
      },
      {
        key: "materialProcess",
        title: <Flex justify="space-between" align="center" gap={8} >
          <SortHeader
            text={t("product.materialProcess")}
            sortKey="materialProcess"
            direction={sortConfig.key === "materialProcess" ? sortConfig.direction : null}
            onSort={handleSortChange}
          />
          <Popover title={t('requirement.processFilter')} trigger={"click"}
            content={
              <Checkbox.Group className="vertical-check-box" options={processes} value={checkedProcess} onChange={value => {
                clearSelection();
                setCheckedProcess(value);
              }} />
            }
          >
            <Button disabled={processes.length === 0} icon={<FilterOutlined />} />
          </Popover>
        </Flex>,
        width: 160,
        render(value, row, rowIndex) {
          return (
            <DetailCellText
              style={row?.deleted === 1 ? DELETED_CELL_STYLE : undefined}
              text={row?.process || row?.product?.process || ""}
            />
          );
        }
      },
      {
        key: "preReady",
        title: <DetailCellText text={t("product.preReady")} />,
        width: 120,
        render(value, row, rowIndex) {
          return (row?.product?.preReady || 0) === 1 ? (
            <Tag color="#87d068">{t("product.isTrue")}</Tag>
          ) : (
            <Tag color="#f50">{t("product.isFalse")}</Tag>
          );
        }
      },
      {
        key: "bomAmount",
        title: <SortHeader
          text={t("requirement.bomAmount")}
          sortKey="bomAmount"
          direction={sortConfig.key === "bomAmount" ? sortConfig.direction : null}
          onSort={handleSortChange}
        />,
        width: 80,
        render(value, row, rowIndex) {
          return <DetailCellText text={row?.bomAmount ? numFormmater(Number(row?.bomAmount)) : ''} />
        }
      },
      {
        key: "useAmount",
        title: <SortHeader
          text={t("requirement.requireCount")}
          sortKey="useAmount"
          direction={sortConfig.key === "useAmount" ? sortConfig.direction : null}
          onSort={handleSortChange}
        />,
        width: 80,
        dataIndex: "useAmount",
        render(value, row, rowIndex) {
          return (
            <DetailCellText
              style={row?.deleted === 1 ? DELETED_CELL_STYLE : undefined}
              text={numFormmater(Number(row?.useAmount || ""))}
            />
          );
        }
      },
      {
        key: "useFactoryCount",
        title: <SortHeader
          text={t("bom.useFactoryCount")}
          sortKey="useFactoryCount"
          direction={sortConfig.key === "useFactoryCount" ? sortConfig.direction : null}
          onSort={handleSortChange}
        />,
        width: 80,
        dataIndex: "useFactoryCount",
        render(value, row, rowIndex) {
          return (
            <DetailCellText
              style={row?.deleted === 1 ? DELETED_CELL_STYLE : undefined}
              text={numFormmater(Number(row?.useFactoryCount || ""))}
            />
          );
        }
      },
      {
        key: "purCount",
        title: <SortHeader
          text={t("requirement.requirmentQuantity")}
          sortKey="purCount"
          direction={sortConfig.key === "purCount" ? sortConfig.direction : null}
          onSort={handleSortChange}
        />,
        width: 80,
        dataIndex: "purCount",
        hidden:
          !!pageId &&
          ![
            PageId.MC_PASSED,
            PageId.MC_REQ_MANAGE,
            PageId.MC_REQ_M,
            PageId.PUR_NOT_PASS,
            PageId.PUR_PASSED
          ].includes(pageId),
        render(value, row, rowIndex) {
          return (
            <DetailCellText
              style={row?.deleted === 1 ? DELETED_CELL_STYLE : undefined}
              text={numFormmater(Number(row?.purCount || ""))}
            />
          );
        }
      },
      {
        key: "puredCount",
        title: <SortHeader
          text={t("requirement.finalPurQuantity")}
          sortKey="puredCount"
          direction={sortConfig.key === "puredCount" ? sortConfig.direction : null}
          onSort={handleSortChange}
        />,
        width: 100,
        dataIndex: "puredCount",
        render(value, row, rowIndex) {
          return (
            <DetailCellText
              style={row?.deleted === 1 ? DELETED_CELL_STYLE : undefined}
              text={numFormmater(Number(row?.puredCount || ""))}
            />
          );
        }
      },
      {
        key: "inCount",
        title: <SortHeader
          text={t("requirement.inCount")}
          sortKey="inCount"
          direction={sortConfig.key === "inCount" ? sortConfig.direction : null}
          onSort={handleSortChange}
        />,
        width: 80,
        dataIndex: "inCount",
        render(value, row, rowIndex) {
          return (
            <DetailCellText
              style={row?.deleted === 1 ? DELETED_CELL_STYLE : undefined}
              text={numFormmater(Number(row?.inCount || ""))}

            />
          );
        }
      },
      {
        key: "outCount",
        title: <SortHeader
          text={t("requirement.outCount")}
          sortKey="outCount"
          direction={sortConfig.key === "outCount" ? sortConfig.direction : null}
          onSort={handleSortChange}
        />,
        width: 80,
        dataIndex: "outCount",
        render(value, row, rowIndex) {
          return (
            <DetailCellText
              style={row?.deleted === 1 ? DELETED_CELL_STYLE : undefined}
              text={numFormmater(Number(row?.outCount || ""))}

            />
          );
        }
      },
      {
        key: "pendingOutCount",
        title: <SortHeader
          text={t("order.toSaledQty")}
          sortKey="pendingOutCount"
          direction={sortConfig.key === "pendingOutCount" ? sortConfig.direction : null}
          onSort={handleSortChange}
        />,
        width: 100,
        render(_, row) {
          return (
            <DetailCellText
              style={row?.deleted === 1 ? DELETED_CELL_STYLE : undefined}
              text={numFormmater(Number(minus(row.useAmount, row.outCount)))}
            />
          );
        }
      },
      {
        key: "preOutQty",
        title: <DetailCellText text={t("bom.preOutNum_1")} />,
        width: 80,
        dataIndex: "outCount",
        render(value, row, rowIndex) {
          return (
            <DetailCellText
              style={row?.deleted === 1 ? DELETED_CELL_STYLE : undefined}
              text={numFormmater(Number(preOutQtyMap[row.id].preOutQty + ''))}
            />
          );
        }
      },
      {
        key: "supplierProgress",
        title: <DetailCellText text={t("requirement.supplierProgress")} />,
        width: 120,
        hidden: !(
          (pageId &&
            [
              PageId.PUR_NOT_PASS,
              PageId.PUR_PASSED,
              PageId.PUR_BE_REJECT,
              PageId.MC_PASSED,
              PageId.PUR_OM
            ].includes(pageId)) ||
          reOrder
        ),
        render: (_, record) =>
          !Number(record?.purCount) ? (
            <Tag>{t("requirement.notQuo")}</Tag>
          ) : (pageId &&
            [
              PageId.PUR_NOT_PASS,
              PageId.PUR_PASSED,
              PageId.PUR_BE_REJECT,
              PageId.MC_PASSED,
              PageId.PUR_OM
            ].includes(pageId)) ||
            reOrder ? (
            <Button
              type="primary"
              onClick={() => onQuoteProgressModal(record)}
            >{`${record.quoteRequestReadCount} / ${record.quoteRequestCount}`}</Button>
          ) : (
            <DetailCellText
              text={`${record.quoteRequestReadCount} / ${record.quoteRequestCount}`}
            />
          )
      },
      {
        key: "prePurchaseOrders",
        title: <DetailCellText text={t("requirement.prePurchaseOrders")} />,
        width: 120,
        hidden: !(
          (pageType && pageType === PageType.PURCHASE) ||
          pageId === PageId.MC_PASSED
        ),
        render: (_, record) =>
          !Number(record?.purCount) ? (
            <Tag>{t("requirement.notPur")}</Tag>
          ) : record.equotationRequest ? (
            <Button onClick={() => onPreviewQuotation(record)} type="link">
              {t("requirement.preview")}
            </Button>
          ) : (
            <DetailCellText text={t("requirement.toGen")} style={PENDING_CELL_STYLE} />
          )
      },
      {
        key: "tags",
        title: <DetailCellText text={t("warehouse.labels")} />,
        width: 200,
        render: (_, row) => <WmsTags refId={row.id} refType={ItemLabelEnum.REQUIREMENT_ITEM}
          tags={row.tags || []} onAddSucc={tag => handelAddItemTag(tag, row.id)} onDelSucc={handleDelItemTag}
        />
      },
      {
        key: "overIssue",
        title: <DetailCellText text={t("common.opt")} />,
        width: 120,
        fixed: "right",
        hidden: !(pageId === PageId.PROD_PASSED),
        render(_, row) {
          return (
            <Button
              type="primary"
              disabled={row?.deleted === RequirementItemDelEnum.DEL}
              onClick={() => setOverIssueItem(row)}
            >
              {t("reimburse.materialOverIssue")}
            </Button>
          );
        }
      },
      {
        key: "edit",
        width: 150,
        title: <DetailCellText text={t("common.opt")} />,
        fixed: "right",
        hidden:
          !!pageId &&
          ![PageId.MC_REQ_MANAGE, PageId.MC_REQ_M, PageId.MC_BE_REJECT].includes(pageId),
        render(_, row) {
          return (
            <Button
              type="primary"
              disabled={row?.deleted === RequirementItemDelEnum.DEL}
              onClick={() => setItemDe(row)}
            >
              {t("common.edit")}
            </Button>
          );
        }
      }
    ],
    [
      noQuotaionPros, pageId, checkedProcess, sortConfig, pageType, reOrder, t,
      selectableFilteredItems, clearSelection, changeCheck, changeItemCheck,
      processes, preOutQtyMap, handleSortChange, handelAddItemTag, handleDelItemTag
    ]
  );

  const tableColumns = useColumnDrag(columns);

  const onQuoteProgressModal = (info: RequirementDetailType) => {
    setProgressInfo({ ...info });
  };
  const onPreviewQuotation = (info: RequirementDetailType) => {
    setQuotationPreviewId(info.id);
  };

  const onUpdatePurCount = async () => {
    //通过
    const onOk = () => {
      setOpen(true);
      setPassLoading(false);
    };

    const onFinally = () => {
      setPassLoading(false);
    };

    setPassLoading(true);
    if (pageId === PageId.PROD_NOT_PASS) {
      const mrd = prodInputRef?.current?.getValues().mrd;
      const eps = prodInputRef?.current?.getValues().eps;
      const preFinishDate = prodInputRef?.current?.getValues().preFinishDate;
      if (!mrd) {
        onFinally();
        return notification.error({
          placement: "top",
          message: t("requirement.plMrd")
        });
      }
      if (!eps) {
        onFinally();
        return notification.error({
          placement: "top",
          message: t("requirement.plEps")
        });
      }
      if (!preFinishDate && detail?.saleOrder) {
        onFinally();
        return notification.error({
          placement: "top",
          message: t("sale.plPreFinishDate")
        });
      }

      if (!detail) return;
      const { items, ...restItems } = detail;
      if (detail?.saleOrder) {
        if (detail.saleOrder) {
          let orderData: SaleOrderAdd = {
            ...detail.saleOrder,
            shippingMethod: detail.saleOrder.shippingMethod || "",
            smtStartDate: eps.valueOf(),
            preFinishDate: preFinishDate ? preFinishDate.valueOf() : null,
            otherNumber1: detail.saleOrder.otherNumber1 ?? "",
            discount: detail.saleOrder.discount ?? "",
            mfgr: detail.saleOrder?.mfgr || "",
            customerModel: detail.saleOrder?.customerModel || "",
            urgencyLevel: detail.saleOrder?.urgencyLevel || "",
            specialReq: detail.saleOrder?.specialReq || ""
          };
          try {
            await orderApis.update(detail.saleOrder.id, orderData);
          } catch (error) {
            onFinally();
            notification.error({
              placement: "top",
              message: t("sale.updateErr")
            });
          }
        }
      }
      apis
        .update(id, {
          ...restItems,
          mrd: mrd.endOf("day").valueOf(),
          eps: eps.valueOf(),
          preFinishDate: preFinishDate ? preFinishDate.valueOf() : null
        })
        .then(() => onOk())
        .finally(() => onFinally());
    } else if (pageId === PageId.PUR_NOT_PASS) {
      let res = await finishQuotationRequest(id);
      handleOptCb(RequestPriceOptCbEnum.FINISH, res, true);
    } else {
      onOk();
    }
    // } else {
    //   if (items?.length && items?.length > 0) {
    //     apis
    //       .itemsUpdate(items)
    //       .then(() => onOk())
    //       .finally(() => onFinally());
    //   }
    // }
  };

  const submitOutInventory = () => {
    setOutInventoryLoading(true);
    const newProducts: Array<OrderAddItem> = [];
    inventory.forEach(item => {
      if (item.quantity > 0) {
        newProducts.push({
          productId: item.productId,
          quantity: item.quantity
        });
      }
    });
    if (newProducts.length === 0) {
      notification.error({
        placement: "top",
        message: t("requirement.noPreOutInventory")
      });
      setOutInventoryLoading(false);
      return;
    }
    const order: OrderAdd = {
      items: newProducts,
      changeType: ChangeEnum.PRE_OUT,
      opType: OperateEnum.PICKING,
      sourceType: SourceTypeEnum.REQUIREMENT,
      sourceId: id,
      scans: [],
      arrivalAt: null,
      orderType: StoreTypeEnum.NUM
    };
    orderAdd({
      order: order,
      orgId: curOrg.id,
      rootCategoryId: ""
    })
      .then(res => {
        notification.success({
          placement: "top",
          message: t("requirement.createPreOutInventorySuccess")
        });
      })
      .catch(err => {
        notification.error({
          placement: "top",
          message: t("requirement.createPreOutInventoryFail")
        });
      })
      .finally(() => {
        setOutInventoryLoading(false);
        setOpenOutInventory(false);
        refresh();
      });
  };

  const handlePurchase = (ids: Array<string>) => {
    handleCancel();
  };

  const handleOptCb = (
    type: RequestPriceOptCbEnum,
    data?: Requirement | undefined,
    passPur?: boolean,
  ) => {
    if (
      type === RequestPriceOptCbEnum.ONE_REQUEST ||
      type === RequestPriceOptCbEnum.MUILTY_REQUEST
    )
      getSubMaterial();
    if (type === RequestPriceOptCbEnum.FINISH && data) {
      let noQuo: string[] = [];
      data.items.forEach(item => {
        if (
          item.deleted !== RequirementItemDelEnum.DEL &&
          !item.equotationRequest &&
          Number(item?.purCount) > 0
        )
          noQuo.push(item.product?.code || "");
      });
      setNoQuotaionPros(noQuo);
      if (!!passPur) {
        setPassLoading(false);
        if (noQuo.length > 0) {
          notification.error({ message: t('warehouse.notPass'), description: t('requirement.noQuotationTip') });
          setOpen(false);
        } else setOpen(true);
      }
    }
  };

  const requirePassCb = (data: string[]) => {
    setNoQuotaionPros(data || []);
  };

  const handleItems = (id: string, data: QuotationRequest) => {
    setItems(last =>
      last.map(it =>
        it.id === id ? { ...it, equotationRequest: data } : { ...it }
      )
    );
  };
  const refresh = () => {
    setNoQuotaionPros([]);
    setNewConfirm([]);
    setOpenPickOut(false);
    getSubMaterial();
  };
  const exportItem = () => {
    const data = items.map(item => {
      let count = 0;
      allOrderItems.filter(oItem => oItem.sourceId === item.id && oItem.order.confirmState !== ConfirmedEnum.CONFIRM)
        .forEach(item => {
          let qty = Number(item.count || 0) || 0;
          count += qty
        })
      // 采购未通过页面导出时补充回填字段，使该文件可直接作为报价导入模板。
      const row: Record<string, string | undefined | number> = {
        ...(pageId === PageId.PUR_NOT_PASS ? {
          [t("requirement.quotationImportRequirementItemId")]: item.id
        } : {}),
        [t("product.code")]: item?.product?.code ?? "",
        [t("product.name")]: item?.product?.name ?? "",
        [t("product.uom")]: item?.product?.uom ?? "",
        [t("product.brand1")]: item?.product?.model ?? "",
        [t("product.materialProcess")]: item?.process ?? item?.product?.process ?? "",
        [t("product.preReady")]:
          item?.product?.preReady === 1
            ? t("product.isTrue")
            : t("product.isFalse"),
        [t("requirement.bomAmount")]: item?.bomAmount ?? "",
        [t("requirement.requireCount")]: item?.useAmount ?? "",
        [t("requirement.requirmentQuantity")]: item?.purCount ?? "",
        [t("requirement.finalPurQuantity")]: item?.puredCount ?? '',
        [t("requirement.inCount")]: item?.inCount ?? "",
        [t("requirement.outCount")]: item?.outCount ?? "",
        [t("order.toSaledQty")]: minus(item.useAmount, item.outCount),
        [t("bom.preOutNum_1")]: count,
        [t("purchase.orderDesc")]: item?.equotationRequest?.remark ?? '',
        ...(pageId === PageId.PUR_NOT_PASS ? {
          [t("requirement.quotationImportSupplierCode")]: "",
          [t("requirement.quotationImportSupplierName")]: "",
          [t("requirement.quotationImportPrice")]: "",
          [t("requirement.quotationImportMpq")]: ""
        } : {})
      };
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
    });
    saveAs(dataBlob, "items.xlsx");
  };

  const handleMultiPurchase = () => {
    setMultiSteps(true);
  };
  const handleSwapSale = () => {
    if (!!!detail?.saleOrder)
      return notification.error({
        message: t('requirement.noSale'),
        placement: "top"
      });
    setSwapSale(true);
  };

  const handleChangeCofirm = useCallback((values: RequirementConfirmLabel[]) => {
    if (detail?.confirms) {
      let isNotPass: boolean = true;
      let newConfirmRes = values.map(val => {
        let value: RequirementConfirmValueEnum | null = null;
        //物控--通过
        if (val.type === RequirementConfirmTypeEnum.MATERIAL_CONTROL) value = RequirementConfirmValueEnum.PASSED
        //其他第一个为show的设置为带评审
        else if (isNotPass && val.show === RequirementConfirmShowEnum.SHOW) {
          value = RequirementConfirmValueEnum.NOT_PASS;
          isNotPass = false
        }
        return {
          ...val,
          // value: val.type === RequirementConfirmTypeEnum.MATERIAL_CONTROL ? RequirementConfirmValueEnum.PASSED : (detail.confirms.find(con => val.type === con.type)?.value ?? null),
          desc: detail.confirms.find(con => val.type === con.type)?.desc ?? '',
          value,
        }
      })
      setNewConfirm(newConfirmRes)
    }
  }, [setNewConfirm, detail?.confirms])

  return (
    <DetailModal
      logBookId={detail?.logBookId}
      modalProps={{
        width: "100vw",
        height: "100vh",
        styles: {
          content: { height: "100vh" },
          body: { height: "calc(100vh - 60px)", padding: 0 }
        },
        title: (
          <DetailTitle
            pageId={pageId}
            detail={detail}
            id={id}
            confirmValue={params?.confirmValue}
            handleOk={handleOk}
            onUpdatePurCount={onUpdatePurCount}
            items={items}
            passLoading={passLoading}
            handlePurchase={handlePurchase}
            onRefresh={refresh}
            reOrder={reOrder}
            canPrint
            preOutQtyMap={preOutQtyMap}
            onDeleteAllItems={() => handleDeleteConfirm(true)}
          />
        ),
        ...props
      }}
    >
      {loading ? (
        <Flex
          style={{ width: "100%", height: "100%" }}
          justify="center"
          align="center"
        >
          <Spin />
        </Flex>
      ) : (
        <>
          {params && open !== null && (
            <RequirementSteps
              visible={!!open}
              onClose={() => setOpen(false)}
              id={id}
              detail={detail}
              order={detail?.saleOrder}
              onOk={() => {
                handleOk && handleOk("del");
                setOpen(() => false);
              }}
              newConfirms={hasConfirmChanged ? newConfirm : undefined}
              confirms={detail?.confirms ?? []}
              requirePassCb={requirePassCb}
              modalProps={{ width: 1000 }}
            />
          )}
          <Flex vertical gap={20} style={{ height: "100%" }}>
            <RequirementReview confirms={detail?.confirms ?? []} isSHowLine />
            <ProdInput pageId={pageId} detail={detail} ref={prodInputRef} />
            <RequirementDesc detail={detail} />
            {pageId === PageId.MC_BE_REJECT && <Form.Item>
              <StepsFormItem value={stepValue} onChange={handleChangeCofirm} />
            </Form.Item>}
            <Flex gap={10} style={{ marginTop: pageId === PageId.MC_BE_REJECT ? -24 : undefined, flexWrap: "wrap" }}>
              {detail?.confirms && requirementIsEnd(detail.confirms) ? (
                <Fragment>
                  {/* {pageId && [PageId.MC_PASSED].includes(pageId) && <Button type="primary" onClick={outInventory}>{t("requirement.createPreOutInventory")}</Button>} */}
                  {pageId && [PageId.MC_PASSED].includes(pageId) && (
                    <>
                      <Button
                        style={{ marginLeft: 10 }}
                        type="primary"
                        onClick={() => setShowList(detail?.id ?? "")}
                      >
                        {t("purchase.outOrder")}
                      </Button>
                      {/* <PreOutOrderPrint
                        reqId={detail.id}
                        order={{} as OrderV2}
                        title={t("requirement.outInventory")}
                        buttonText={t("purchase.outPrint")}
                        requirement={detail}
                      /> */}
                    </>
                  )}
                </Fragment>
              ) : (
                detail &&
                pageId &&
                [PageId.MC_PASSED].includes(pageId) &&
                detail?.mrd && (
                  <RequestPriceOpt
                    detail={detail}
                    id={detail?.id}
                    mrd={detail?.mrd}
                    items={detail?.items}
                    cb={handleOptCb}
                  />
                )
              )}
              {detail?.confirms && pageId && [PageId.MC_REQ_MANAGE, PageId.MC_REQ_M].includes(pageId) && (
                <Fragment>
                  {detail?.mrd && pageId !== PageId.MC_REQ_M && (
                    <RequestPriceOpt
                      detail={detail}
                      id={detail?.id}
                      mrd={detail?.mrd}
                      items={detail?.items}
                      cb={handleOptCb}
                    />
                  )}
                  {/* <Button type="primary" onClick={outInventory}>{t("requirement.createPreOutInventory")}</Button> */}
                  <Button
                    style={{ marginLeft: 10 }}
                    type="primary"
                    onClick={() => setShowList(detail?.id ?? "")}
                  >
                    {t("purchase.outOrder")}
                  </Button>
                </Fragment>
              )}
              {/* {pageId &&
                [PageId.MC_REQ_MANAGE, PageId.MC_PASSED].includes(pageId) && (
                  <Button
                    type="primary"
                    onClick={() => setSpecialOut(detail?.id ?? "")}
                  >
                    {t("warehouse.specialMaterialList")}
                  </Button>
                )} */}
              {pageId &&
                [PageId.MC_REQ_MANAGE, PageId.MC_REQ_M, PageId.MC_PASSED].includes(pageId) && (
                  <Button
                    type="primary"
                    onClick={() => setOpenPickOut(true)}
                    disabled={selectedRows.length === 0}
                  >
                    {t("requirement.prePickOut")}
                  </Button>
                )}

              {pageId &&
                [PageId.PUR_NOT_PASS, PageId.MC_REQ_MANAGE].includes(
                  pageId
                ) && (
                  <Button
                    disabled={selectedRows.length === 0}
                    onClick={handleMultiPurchase}
                  >
                    {t("requirement.multiPurchaseTitle")}
                  </Button>
                )}
              {pageId &&
                [PageId.PUR_NOT_PASS, PageId.MC_REQ_MANAGE].includes(
                  pageId
                ) && (
                  <Button
                    disabled={selectedRows.length === 0}
                    onClick={handleSwapSale}
                  >
                    {t("requirement.swapSale")}
                  </Button>
                )}

              <Fragment>
                {pageId &&
                  [PageId.MC_REQ_MANAGE, PageId.MC_REQ_M, PageId.MC_BE_REJECT].includes(
                    pageId
                  ) && (
                    <Button
                      type="primary"
                      onClick={() => {
                        setOpenAddItem(true);
                      }}
                    >
                      {t("common.add")}
                    </Button>
                  )}
                {pageId &&
                  [PageId.MC_REQ_MANAGE, PageId.MC_REQ_M, PageId.MC_BE_REJECT].includes(
                    pageId
                  ) && (
                    <Button
                      size="small"
                      danger
                      disabled={selectedIds.length === 0}
                      icon={<DeleteFilled />}
                      style={{ marginLeft: 10 }}
                      onClick={() => {
                        setDeleteModalOpen(true);
                      }}
                    >
                      {t("common.del")}
                    </Button>
                  )}
              </Fragment>
              <Button type="link" onClick={exportItem}>
                {t("requirement.exportExcel")}
              </Button>
              {/* 报价 Excel 的导入入口仅在采购未通过阶段开放。 */}
              {pageId === PageId.PUR_NOT_PASS && detail && (
                <ImportQuotationExcel
                  requirement={detail}
                  items={items}
                  onSuccess={refresh}
                />
              )}
              {
                pageId && [PageId.PUR_NOT_PASS, PageId.MC_REQ_MANAGE].includes(pageId) && (
                  <Button type="dashed" onClick={() => setMultiQuotation(true)}>
                    {t('requirement.batchSelect')}
                  </Button>
                )
              }
              <MaterialSearchWrap>
                <ListFilterBar
                  onSearch={() => { clearSelection(); setSearchKeyword(keyword); }}
                  onReset={() => { clearSelection(); setKeyword(""); setSearchKeyword(""); }}
                  showReset={false}
                >
                  {({ submit }) => (
                    <Input allowClear value={keyword} onChange={event => setKeyword(event.target.value)} onPressEnter={submit}
                      placeholder={`${t("product.code")}/${t("product.name")}/${t("product.model")}`}
                      aria-label={t("requirement.searchPro")} />
                  )}
                </ListFilterBar>
              </MaterialSearchWrap>
            </Flex>
            <Flex style={{ margin: "-16px 0" }} gap={6}>
              {noQuotaionPros && noQuotaionPros.length > 0 && (
                <div style={{ color: "red" }}>
                  {t("requirement.noQuotationTip")}
                </div>
              )}
              {isRepTip && (
                <div style={{ color: "orange" }}>
                  {t("requirement.duplicateMaterialTip")}
                </div>
              )}
            </Flex>
            <TableWrapNew ref={tableWrapRef}>
              <CommonTable<RequirementDetailType>
                rowKey="id"
                columns={tableColumns}
                dataSource={filteredItems}
                scrollHeight={scrollHeight}
                virtual={virtualTable}
              />
            </TableWrapNew>
          </Flex>
          {progressInfo && detail && (
            <QuoteProgressDetail
              requirement={detail}
              mrd={detail.mrd}
              detail={progressInfo}
              onCancel={() => setProgressInfo(undefined)}
              reOrder={reOrder}
              onReSelect={handleItems}
            />
          )}
          {quotationPreviewId && detail && (
            <PurchaseModal
              reqNumber={detail?.number ?? ""}
              id={detail.id}
              itemId={quotationPreviewId}
              open={true}
              onOk={res => setQuotationPreviewId("")}
              onCancel={() => setQuotationPreviewId("")}
            />
          )}
        </>
      )}
      <Modal
        title={t("requirement.preOutInventory")}
        open={openOutInventory}
        onCancel={() => setOpenOutInventory(false)}
        onClose={() => {
          setOpenOutInventory(false);
          setInventoryList([]);
        }}
        width={1000}
        onOk={submitOutInventory}
        loading={outInventoryLoading}
      >
        <div style={{ maxHeight: 500, overflow: "auto" }}>
          <List
            size="small"
            bordered
            dataSource={inventory}
            renderItem={item => (
              <List.Item>
                <div>
                  {t("requirement.goodsName")}：{item.name}
                </div>
                <div>
                  {t("requirement.availableQuantity")}：{item.quantity}
                </div>
              </List.Item>
            )}
          />
        </div>
      </Modal>
      {!!showList && (
        <InventoryList
          fetchId={showList}
          fetchType="REQ"
          requirement={detail}
          number={detail?.number ?? ""}
          onClose={() => setShowList("")}
          preOutQtyMap={preOutQtyMap}
        />
      )}
      {!!specialOut && (
        <SpecialOut
          showItems={items}
          reqNumber={detail?.number ?? ""}
          reqId={specialOut}
          visible={!!specialOut}
          onClose={() => setSpecialOut("")}
        />
      )}
      {openAddItem !== null && <AddItem
        open={!!openAddItem}
        setOpen={setOpenAddItem}
        reqId={id}
        onSuccess={(newItems, orderChanges) => {
          setItems([...items, ...newItems]);
          const updatedOrder = orderChanges?.updatedOrder;
          const createdOrder = orderChanges?.createdOrder;
          if (updatedOrder) {
            setOrders(last =>
              last.map(order => order.id === updatedOrder.id ? updatedOrder : order)
            );
          } else if (createdOrder) {
            setOrders(last => [...last, createdOrder]);
          }
        }}
      />}
      {itemDe && (
        <ItemInfo
          item={itemDe}
          onClose={() => setItemDe(null)}
          onSuccessCb={data => {
            setItems(
              items.map(itm => ({
                ...itm,
                useAmount: itm.id === data.id ? data.useAmount : itm.useAmount,
                purCount: itm.id === data.id ? data.purCount : itm.purCount,
                puredCount: itm.id === data.id ? data.purCount : itm.puredCount,
                bomAmount: itm.id === data.id ? data.bomAmount : itm.bomAmount,
              }))
            );
            setItemDe(null);
          }}
        />
      )}
      <Modal
        title="删除子件中..."
        open={deleteProgressOpen}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        zIndex={9999}
      >
        <Flex style={{ marginTop: 24, marginBottom: 24 }} justify="center">
          <Spin spinning={deleteProgressOpen} size="large" />
        </Flex>
        <Progress percent={deleteProgress} />
      </Modal>
      {deleteModalOpen !== null && <DeleteModal
        open={!!deleteModalOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteModalOpen(false)}
      />}
      {multiSteps && (
        <MultiPurchaseSteps
          mrd={detail?.mrd || null}
          open={multiSteps}
          items={selectedRows.filter(
            item =>
              Number(item.purCount) > 0 &&
              !!!item.equotationRequest?.purchaseItem
          )}
          onClose={needRefresh => {
            setMultiSteps(false);
            if (needRefresh) refresh();
          }}
        />
      )}
      {swapSale && detail?.saleOrder && (
        <SwapSaleOrder
          open={swapSale}
          requirement={detail}
          handleOk={refresh}
          items={selectedRows.filter(
            item =>
              Number(item.purCount) > 0 &&
              !!!item.equotationRequest?.purchaseItem
          )}
          onClose={() => setSwapSale(false)}
          saleOrder={detail.saleOrder}
        />
      )}
      {
        detail && multiQuotation && <MultiQuotationOpt
          open={multiQuotation}
          onClose={() => setMultiQuotation(false)}
          onSuccess={() => { }}
          requirementId={detail.id}
        />
      }
      {
        openPickOut && <RequirementPickOut selectedRows={selectedRows.filter(row => !row.product?.preReady && Number(minus(row.useAmount, row.outCount)) > 0)} preOutQtyMap={preOutQtyMap}
          requirement={detail}
          onClose={() => setOpenPickOut(false)}
          onSuccess={refresh} />
      }
      {overIssueItem && detail && (
        <AddMaterialOverIssue
          open={!!overIssueItem}
          requirementId={detail.id}
          requirementNumber={detail.number}
          initialItem={{
            requirementItemId: overIssueItem.id,
            productId: overIssueItem.product?.id,
          }}
          onClose={() => setOverIssueItem(null)}
          onSuccess={() => setOverIssueItem(null)}
        />
      )}
    </DetailModal>
  );
};

export default RequirementDetail;
